import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, query, orderBy, getDocs, doc, getDoc, updateDoc, addDoc, deleteDoc, where, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Navbar from '../components/Navbar';

// Map of province codes to full names
const provinceNames: { [key: string]: string } = {
  'AB': 'Alberta',
  'BC': 'British Columbia',
  'MB': 'Manitoba',
  'NB': 'New Brunswick',
  'NL': 'Newfoundland and Labrador',
  'NS': 'Nova Scotia',
  'NT': 'Northwest Territories',
  'NU': 'Nunavut',
  'ON': 'Ontario',
  'PE': 'Prince Edward Island',
  'QC': 'Quebec',
  'SK': 'Saskatchewan',
  'YT': 'Yukon'
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { userDetails, isAdmin, currentUser } = useAuth();
  
  // Dashboard data
  const [totalMiles, setTotalMiles] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [totalTeams, setTotalTeams] = useState<number>(0);
  const [provincesRanking, setProvincesRanking] = useState<any[]>([]);
  const [topTeams, setTopTeams] = useState<any[]>([]);
  const [topIndividuals, setTopIndividuals] = useState<any[]>([]);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  
  // Admin actions
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [milesAmount, setMilesAmount] = useState<string>('');
  const [milesDate, setMilesDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [actionSuccess, setActionSuccess] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string>('');
  
  // New admin action states
  const [userToDelete, setUserToDelete] = useState<string>('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<string>('');
  const [userProvinceChange, setUserProvinceChange] = useState<string>('');
  const [newUserProvince, setNewUserProvince] = useState<string>('');
  const [teamToUpdate, setTeamToUpdate] = useState<string>('');
  const [newTeamProvince, setNewTeamProvince] = useState<string>('');
  const [allUsersList, setAllUsersList] = useState<any[]>([]);
  const [allTeamsList, setAllTeamsList] = useState<any[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  // Component state
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'export' | 'actions'>('dashboard');
  
  // Check admin access
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);
  
  // Fetch admin dashboard data
  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        // Get overall statistics
        const statsDoc = await getDoc(doc(db, 'statistics', 'totalMiles'));
        if (statsDoc.exists()) {
          setTotalMiles(statsDoc.data().miles || 0);
        }
        
        // Get user count
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        setTotalUsers(usersSnapshot.size);
        
        // Get recent users and all users for admin actions
        const recentUsersQuery = query(usersRef, orderBy('createdAt', 'desc'));
        const recentUsersSnapshot = await getDocs(recentUsersQuery);
        
        const recentUsersList: any[] = [];
        const allUsers: any[] = [];
        
        recentUsersSnapshot.forEach((doc) => {
          const data = doc.data();
          // For recent users list (limited to 10)
          if (recentUsersList.length < 10) {
            recentUsersList.push({
              id: doc.id,
              name: `${data.firstName} ${data.lastName}`,
              email: data.email || 'Unknown',
              province: provinceNames[data.province] || data.province,
              createdAt: data.createdAt
            });
          }
          
          // For all users list (used in admin actions)
          allUsers.push({
            id: doc.id,
            name: `${data.firstName} ${data.lastName}`,
            email: data.email || 'Unknown',
            province: data.province,
            provinceDisplay: provinceNames[data.province] || data.province,
            teamId: data.teamId
          });
        });
        
        setRecentUsers(recentUsersList);
        setAllUsersList(allUsers);
        
        // Get team count and all teams
        const teamsRef = collection(db, 'teams');
        const teamsSnapshot = await getDocs(teamsRef);
        setTotalTeams(teamsSnapshot.size);
        
        const allTeams: any[] = [];
        teamsSnapshot.forEach((doc) => {
          const data = doc.data();
          allTeams.push({
            id: doc.id,
            name: data.name,
            province: data.province,
            provinceDisplay: provinceNames[data.province] || data.province,
            memberCount: data.memberCount || 0
          });
        });
        
        setAllTeamsList(allTeams);
        
        // Get provinces ranking
        const provincesRef = collection(db, 'provinces');
        const provincesQuery = query(provincesRef, orderBy('totalMiles', 'desc'));
        const provincesSnapshot = await getDocs(provincesQuery);
        
        const provincesList: any[] = [];
        provincesSnapshot.forEach((doc) => {
          const data = doc.data();
          provincesList.push({
            id: doc.id,
            name: provinceNames[doc.id] || doc.id,
            miles: data.totalMiles || 0,
            participantCount: data.participantCount || 0
          });
        });
        
        setProvincesRanking(provincesList);
        
        // Get top 5 teams
        const topTeamsQuery = query(teamsRef, orderBy('totalMiles', 'desc'));
        const topTeamsSnapshot = await getDocs(topTeamsQuery);
        
        const teamsList: any[] = [];
        topTeamsSnapshot.forEach((doc) => {
          if (teamsList.length < 5) {
            const data = doc.data();
            teamsList.push({
              id: doc.id,
              name: data.name,
              province: provinceNames[data.province] || data.province,
              miles: data.totalMiles || 0,
              memberCount: data.memberCount || 0
            });
          }
        });
        
        setTopTeams(teamsList);
        
        // Get top 5 individuals
        const topUsersQuery = query(usersRef, orderBy('totalMiles', 'desc'));
        const topUsersSnapshot = await getDocs(topUsersQuery);
        
        const usersList: any[] = [];
        topUsersSnapshot.forEach((doc) => {
          if (usersList.length < 5) {
            const data = doc.data();
            usersList.push({
              id: doc.id,
              name: `${data.firstName} ${data.lastName}`,
              province: provinceNames[data.province] || data.province,
              miles: data.totalMiles || 0
            });
          }
        });
        
        setTopIndividuals(usersList);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        setLoading(false);
      }
    };
    
    if (isAdmin) {
      fetchAdminData();
    }
  }, [isAdmin, navigate]);
  
  // Handle admin action: Log miles for user
  const handleLogMilesForUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      setActionError('Please select a user');
      return;
    }
    
    if (!milesAmount || isNaN(Number(milesAmount)) || Number(milesAmount) <= 0) {
      setActionError('Please enter a valid number of miles');
      return;
    }
    
    try {
      setActionLoading(true);
      setActionError('');
      
      // Add miles entry for the selected user
      await addDoc(collection(db, 'miles'), {
        userId: selectedUserId,
        miles: Number(milesAmount),
        date: milesDate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        addedByAdmin: true,
        adminId: currentUser?.uid
      });
      
      // Display success message
      setActionSuccess(true);
      setMilesAmount('');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error logging miles for user:', error);
      setActionError('Failed to log miles for user');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle export data
  const handleExportData = async (dataType: 'users' | 'teams' | 'miles' | 'complete') => {
    try {
      setActionLoading(true);
      setActionError('');
      
      // Prepare CSV header and data based on export type
      let csvData = '';
      let filename = '';
      
      if (dataType === 'complete') {
        // Get all users with their complete information
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        // Get all teams for lookup
        const teamsRef = collection(db, 'teams');
        const teamsSnapshot = await getDocs(teamsRef);
        const teamsMap = new Map();
        teamsSnapshot.forEach(doc => {
          teamsMap.set(doc.id, doc.data());
        });
        
        // CSV header with all fields
        csvData = 'User ID,First Name,Last Name,Email,Province,Province Name,Date of Birth,Team ID,Team Name,Registration Date,Total Miles,Miles Entries,Is Admin\n';
        
        // Process each user with comprehensive data
        const userPromises = usersSnapshot.docs.map(async (doc) => {
          const userData = doc.data();
          const userId = doc.id;
          
          // Get user's total miles and all miles entries
          const milesRef = collection(db, 'miles');
          const milesQuery = query(milesRef, where('userId', '==', userId));
          const milesSnapshot = await getDocs(milesQuery);
          
          let totalMiles = 0;
          const milesEntries: string[] = [];
          
          milesSnapshot.forEach((mileDoc) => {
            const mileData = mileDoc.data();
            totalMiles += Number(mileData.miles);
            milesEntries.push(`${mileData.date}:${mileData.miles}`);
          });
          
          // Get team name if user has a team
          let teamName = '';
          if (userData.teamId && teamsMap.has(userData.teamId)) {
            teamName = teamsMap.get(userData.teamId).name || '';
          }
          
          // Format CSV row with all data
          return `${userId},${userData.firstName || ''},${userData.lastName || ''},${userData.email || ''},${userData.province || ''},${provinceNames[userData.province] || ''},${userData.dateOfBirth || ''},${userData.teamId || ''},${teamName},${userData.createdAt || ''},${totalMiles},${milesEntries.join('|')},${userData.isAdmin ? 'Yes' : 'No'}`;
        });
        
        const userRows = await Promise.all(userPromises);
        csvData += userRows.join('\n');
        filename = 'million-miles-may-complete-data.csv';
      }
      else if (dataType === 'users') {
        // Get all users
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        // CSV header
        csvData = 'User ID,First Name,Last Name,Email,Province,Team ID,Registration Date,Total Miles\n';
        
        // Process each user
        const userPromises = usersSnapshot.docs.map(async (doc) => {
          const userData = doc.data();
          const userId = doc.id;
          
          // Get user's total miles
          const milesRef = collection(db, 'miles');
          const milesQuery = query(milesRef, where('userId', '==', userId));
          const milesSnapshot = await getDocs(milesQuery);
          
          let totalMiles = 0;
          milesSnapshot.forEach((mileDoc) => {
            totalMiles += Number(mileDoc.data().miles);
          });
          
          // Format CSV row
          return `${userId},${userData.firstName},${userData.lastName},${userData.email || ''},${userData.province},${userData.teamId || ''},${userData.createdAt},${totalMiles}`;
        });
        
        const userRows = await Promise.all(userPromises);
        csvData += userRows.join('\n');
        filename = 'million-miles-may-users.csv';
        
      } else if (dataType === 'teams') {
        // Get all teams
        const teamsRef = collection(db, 'teams');
        const teamsSnapshot = await getDocs(teamsRef);
        
        // CSV header
        csvData = 'Team ID,Team Name,Province,Member Count,Total Miles,Created By,Creation Date\n';
        
        // Process each team
        teamsSnapshot.forEach((doc) => {
          const teamData = doc.data();
          // Format CSV row
          csvData += `${doc.id},${teamData.name},${teamData.province},${teamData.memberCount || 0},${teamData.totalMiles || 0},${teamData.createdBy},${teamData.createdAt}\n`;
        });
        
        filename = 'million-miles-may-teams.csv';
        
      } else if (dataType === 'miles') {
        // Get all miles entries
        const milesRef = collection(db, 'miles');
        const milesSnapshot = await getDocs(milesRef);
        
        // CSV header
        csvData = 'Entry ID,User ID,Miles,Date,Added By Admin,Admin ID,Created At\n';
        
        // Process each miles entry
        milesSnapshot.forEach((doc) => {
          const milesData = doc.data();
          // Format CSV row
          csvData += `${doc.id},${milesData.userId},${milesData.miles},${milesData.date},${milesData.addedByAdmin ? 'Yes' : 'No'},${milesData.adminId || ''},${milesData.createdAt}\n`;
        });
        
        filename = 'million-miles-may-miles.csv';
      }
      
      // Trigger download
      if (csvData) {
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setSuccessMessage(`${dataType} data exported successfully!`);
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
      
    } catch (error) {
      console.error(`Error exporting ${dataType} data:`, error);
      setActionError(`Failed to export ${dataType} data. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle delete user
  const handleDeleteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userToDelete) {
      setActionError('Please select a user to delete');
      return;
    }
    
    if (deleteConfirmation !== 'DELETE') {
      setActionError('Please type DELETE to confirm');
      return;
    }
    
    try {
      setActionLoading(true);
      setActionError('');
      
      // Get user details first for province updates
      const userRef = doc(db, 'users', userToDelete);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        setActionError('User not found');
        setActionLoading(false);
        return;
      }
      
      const userData = userDoc.data();
      const userProvince = userData.province;
      const userTeamId = userData.teamId;
      
      // Delete miles entries for this user
      const milesRef = collection(db, 'miles');
      const milesQuery = query(milesRef, where('userId', '==', userToDelete));
      const milesSnapshot = await getDocs(milesQuery);
      
      const deleteMilesPromises = milesSnapshot.docs.map(async (doc) => {
        await deleteDoc(doc.ref);
      });
      
      await Promise.all(deleteMilesPromises);
      
      // Update team member count if user is in a team
      if (userTeamId) {
        const teamRef = doc(db, 'teams', userTeamId);
        const teamDoc = await getDoc(teamRef);
        
        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          const newMemberCount = Math.max(0, (teamData.memberCount || 1) - 1);
          
          await updateDoc(teamRef, {
            memberCount: newMemberCount
          });
        }
      }
      
      // Update province participant count
      const provinceRef = doc(db, 'provinces', userProvince);
      const provinceDoc = await getDoc(provinceRef);
      
      if (provinceDoc.exists()) {
        const provinceData = provinceDoc.data();
        const newParticipantCount = Math.max(0, (provinceData.participantCount || 1) - 1);
        
        await updateDoc(provinceRef, {
          participantCount: newParticipantCount
        });
      }
      
      // Finally delete the user
      await deleteDoc(userRef);
      
      // Show success message
      setSuccessMessage(`User ${userData.firstName} ${userData.lastName} deleted successfully`);
      setUserToDelete('');
      setDeleteConfirmation('');
      
      // Remove user from the list
      setAllUsersList(prevUsers => prevUsers.filter(user => user.id !== userToDelete));
      setRecentUsers(prevUsers => prevUsers.filter(user => user.id !== userToDelete));
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error deleting user:', error);
      setActionError('Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle change user province
  const handleChangeUserProvince = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userProvinceChange) {
      setActionError('Please select a user');
      return;
    }
    
    if (!newUserProvince) {
      setActionError('Please select a new province');
      return;
    }
    
    try {
      setActionLoading(true);
      setActionError('');
      
      // Get user current details
      const userRef = doc(db, 'users', userProvinceChange);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        setActionError('User not found');
        setActionLoading(false);
        return;
      }
      
      const userData = userDoc.data();
      const oldProvince = userData.province;
      
      // Skip if province is the same
      if (oldProvince === newUserProvince) {
        setActionError('User is already in this province');
        setActionLoading(false);
        return;
      }
      
      // Update old province participant count
      const oldProvinceRef = doc(db, 'provinces', oldProvince);
      const oldProvinceDoc = await getDoc(oldProvinceRef);
      
      if (oldProvinceDoc.exists()) {
        const provinceData = oldProvinceDoc.data();
        const newParticipantCount = Math.max(0, (provinceData.participantCount || 1) - 1);
        
        await updateDoc(oldProvinceRef, {
          participantCount: newParticipantCount
        });
      }
      
      // Update new province participant count
      const newProvinceRef = doc(db, 'provinces', newUserProvince);
      const newProvinceDoc = await getDoc(newProvinceRef);
      
      if (newProvinceDoc.exists()) {
        const provinceData = newProvinceDoc.data();
        await updateDoc(newProvinceRef, {
          participantCount: (provinceData.participantCount || 0) + 1
        });
      } else {
        // Create new province document if it doesn't exist
        await setDoc(newProvinceRef, {
          totalMiles: 0,
          participantCount: 1
        });
      }
      
      // Update user province
      await updateDoc(userRef, {
        province: newUserProvince
      });
      
      // Update user in lists
      setAllUsersList(prevUsers => 
        prevUsers.map(user => 
          user.id === userProvinceChange 
            ? {...user, province: newUserProvince, provinceDisplay: provinceNames[newUserProvince] || newUserProvince} 
            : user
        )
      );
      
      setRecentUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userProvinceChange 
            ? {...user, province: provinceNames[newUserProvince] || newUserProvince} 
            : user
        )
      );
      
      // Show success message
      setSuccessMessage(`User province updated successfully to ${provinceNames[newUserProvince] || newUserProvince}`);
      setUserProvinceChange('');
      setNewUserProvince('');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error updating user province:', error);
      setActionError('Failed to update user province');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle change team province
  const handleChangeTeamProvince = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!teamToUpdate) {
      setActionError('Please select a team');
      return;
    }
    
    if (!newTeamProvince) {
      setActionError('Please select a new province');
      return;
    }
    
    try {
      setActionLoading(true);
      setActionError('');
      
      // Get team current details
      const teamRef = doc(db, 'teams', teamToUpdate);
      const teamDoc = await getDoc(teamRef);
      
      if (!teamDoc.exists()) {
        setActionError('Team not found');
        setActionLoading(false);
        return;
      }
      
      const teamData = teamDoc.data();
      
      // Skip if province is the same
      if (teamData.province === newTeamProvince) {
        setActionError('Team is already in this province');
        setActionLoading(false);
        return;
      }
      
      // Update team province
      await updateDoc(teamRef, {
        province: newTeamProvince
      });
      
      // Update team in all teams list
      setAllTeamsList(prevTeams => 
        prevTeams.map(team => 
          team.id === teamToUpdate 
            ? {...team, province: newTeamProvince, provinceDisplay: provinceNames[newTeamProvince] || newTeamProvince} 
            : team
        )
      );
      
      // Show success message
      setSuccessMessage(`Team province updated successfully to ${provinceNames[newTeamProvince] || newTeamProvince}`);
      setTeamToUpdate('');
      setNewTeamProvince('');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error updating team province:', error);
      setActionError('Failed to update team province');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Add the syncEmailsFromAuth function inside the component
  const syncEmailsFromAuth = async () => {
    try {
      setActionLoading(true);
      
      // Show detailed instructions to the admin
      alert(`To sync email addresses from Firebase Auth to Firestore, follow these steps:

1. Run this command to install the Firebase Admin SDK: npm install firebase-admin
2. Go to Firebase Console > Project Settings > Service Accounts
3. Click "Generate new private key" and save the file as serviceAccountKey.json in the project root
4. Run this command: node src/scripts/sync-emails.js
5. After the script completes, all user emails will be visible in exports

This is a one-time operation that will ensure all user emails are stored in Firestore.`);
    } catch (err: any) {
      console.error('Error:', err);
      setActionError('An error occurred. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage the Million Miles May challenge
          </p>
        </div>
        
        {/* Admin Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`
                  ${activeTab === 'dashboard'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`
                  ${activeTab === 'users'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Recent Users
              </button>
              <button
                onClick={() => setActiveTab('actions')}
                className={`
                  ${activeTab === 'actions'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Admin Actions
              </button>
              <button
                onClick={() => setActiveTab('export')}
                className={`
                  ${activeTab === 'export'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Export Data
              </button>
            </nav>
          </div>
        </div>
        
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Stats Overview */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Challenge Overview</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Miles
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-primary">
                      {totalMiles.toLocaleString()}
                    </dd>
                    <dd className="mt-2 text-sm text-gray-500">
                      {((totalMiles / 1000000) * 100).toFixed(2)}% of goal (1,000,000 miles)
                    </dd>
                  </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Participants
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-secondary">
                      {totalUsers.toLocaleString()}
                    </dd>
                    <dd className="mt-2 text-sm text-gray-500">
                      {totalUsers > 0 ? `${(totalMiles / totalUsers).toFixed(1)} miles per person` : '0 miles per person'}
                    </dd>
                  </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Teams
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-accent">
                      {totalTeams.toLocaleString()}
                    </dd>
                    <dd className="mt-2 text-sm text-gray-500">
                      {totalTeams > 0 ? `${(totalUsers / totalTeams).toFixed(1)} participants per team` : '0 participants per team'}
                    </dd>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Provincial Map & Rankings */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Provincial Rankings</h2>
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rank
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Province/Territory
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Participants
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Miles
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Miles per Participant
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {provincesRanking.map((province, index) => (
                      <tr key={province.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {province.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {province.participantCount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {province.miles.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {province.participantCount > 0 
                            ? (province.miles / province.participantCount).toFixed(1) 
                            : '0.0'}
                        </td>
                      </tr>
                    ))}
                    {provincesRanking.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Top Teams & Individuals */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Top Teams */}
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Top Teams</h3>
                </div>
                <div className="border-t border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rank
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Team
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Miles
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {topTeams.map((team, index) => (
                        <tr key={team.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              {team.name}
                              <p className="text-xs text-gray-500">{team.province} • {team.memberCount} members</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {team.miles.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      {topTeams.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Top Individuals */}
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Top Individuals</h3>
                </div>
                <div className="border-t border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rank
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Miles
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {topIndividuals.map((individual, index) => (
                        <tr key={individual.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              {individual.name}
                              <p className="text-xs text-gray-500">{individual.province}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {individual.miles.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      {topIndividuals.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Recent Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Registrations</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                The 10 most recent user registrations
              </p>
            </div>
            <div className="border-t border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Province
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registered
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.province}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {recentUsers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                        No recent registrations
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Admin Actions Tab */}
        {activeTab === 'actions' && (
          <div className="grid grid-cols-1 gap-6">
            {/* Existing admin actions */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Admin Actions</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Perform administrative tasks for the challenge
              </p>
            </div>
            
            {successMessage && (
              <div className="mx-6 mt-4 bg-green-50 border-l-4 border-green-500 p-4">
                <p className="text-green-700">{successMessage}</p>
              </div>
            )}
            
            {actionError && (
              <div className="mx-6 mt-4 bg-red-50 border-l-4 border-red-500 p-4">
                <p className="text-red-700">{actionError}</p>
              </div>
            )}
            
            <div className="border-t border-gray-200">
              <div className="px-4 py-5 sm:px-6">
                <div className="grid grid-cols-1 gap-y-8 gap-x-6 md:grid-cols-2">
                  {/* Log Miles Form - Existing */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">Log Miles for User</h4>
                    
                    {actionSuccess && (
                      <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4">
                        <p className="text-green-700">Successfully logged miles for user!</p>
                      </div>
                    )}
                    
                    <form onSubmit={handleLogMilesForUser} className="space-y-4">
                      <div>
                        <label htmlFor="userId" className="block text-sm font-medium text-gray-700">
                          Select User
                        </label>
                        <div className="mt-1">
                          <select
                            id="userId"
                            name="userId"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            required
                          >
                            <option value="">Select a user</option>
                            {recentUsers.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.name} ({user.email})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="miles" className="block text-sm font-medium text-gray-700">
                          Miles
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            name="miles"
                            id="miles"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="0.0"
                            min="0.1"
                            step="0.1"
                            value={milesAmount}
                            onChange={(e) => setMilesAmount(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                          Date
                        </label>
                        <div className="mt-1">
                          <input
                            type="date"
                            name="date"
                            id="date"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            value={milesDate}
                            onChange={(e) => setMilesDate(e.target.value)}
                            max={new Date().toISOString().split('T')[0]}
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <button
                          type="submit"
                          disabled={actionLoading}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          {actionLoading ? 'Saving...' : 'Log Miles'}
                        </button>
                      </div>
                    </form>
                  </div>
                  
                  {/* Delete User Form - New */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">Delete User Account</h4>
                    <form onSubmit={handleDeleteUser} className="space-y-4">
                      <div>
                        <label htmlFor="userToDelete" className="block text-sm font-medium text-gray-700">
                          Select User to Delete
                        </label>
                        <div className="mt-1">
                          <select
                            id="userToDelete"
                            name="userToDelete"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            value={userToDelete}
                            onChange={(e) => setUserToDelete(e.target.value)}
                            required
                          >
                            <option value="">Select a user</option>
                            {allUsersList.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.name} ({user.email})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="deleteConfirmation" className="block text-sm font-medium text-gray-700">
                          Type DELETE to confirm
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="deleteConfirmation"
                            id="deleteConfirmation"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            value={deleteConfirmation}
                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                            required
                          />
                        </div>
                        <p className="mt-1 text-xs text-red-500">
                          Warning: This action cannot be undone. All user data will be permanently deleted.
                        </p>
                      </div>
                      
                      <div>
                        <button
                          type="submit"
                          disabled={actionLoading || deleteConfirmation !== 'DELETE'}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        >
                          {actionLoading ? 'Deleting...' : 'Delete User'}
                        </button>
                      </div>
                    </form>
                  </div>
                  
                  {/* Change User Province Form - New */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">Change User Province</h4>
                    <form onSubmit={handleChangeUserProvince} className="space-y-4">
                      <div>
                        <label htmlFor="userProvinceChange" className="block text-sm font-medium text-gray-700">
                          Select User
                        </label>
                        <div className="mt-1">
                          <select
                            id="userProvinceChange"
                            name="userProvinceChange"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            value={userProvinceChange}
                            onChange={(e) => setUserProvinceChange(e.target.value)}
                            required
                          >
                            <option value="">Select a user</option>
                            {allUsersList.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.name} - Current: {user.provinceDisplay}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="newUserProvince" className="block text-sm font-medium text-gray-700">
                          New Province/Territory
                        </label>
                        <div className="mt-1">
                          <select
                            id="newUserProvince"
                            name="newUserProvince"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            value={newUserProvince}
                            onChange={(e) => setNewUserProvince(e.target.value)}
                            required
                          >
                            <option value="">Select Province/Territory</option>
                            {Object.entries(provinceNames).map(([code, name]) => (
                              <option key={code} value={code}>{name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <button
                          type="submit"
                          disabled={actionLoading}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          {actionLoading ? 'Updating...' : 'Update Province'}
                        </button>
                      </div>
                    </form>
                  </div>
                  
                  {/* Change Team Province Form - New */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">Change Team Province</h4>
                    <form onSubmit={handleChangeTeamProvince} className="space-y-4">
                      <div>
                        <label htmlFor="teamToUpdate" className="block text-sm font-medium text-gray-700">
                          Select Team
                        </label>
                        <div className="mt-1">
                          <select
                            id="teamToUpdate"
                            name="teamToUpdate"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            value={teamToUpdate}
                            onChange={(e) => setTeamToUpdate(e.target.value)}
                            required
                          >
                            <option value="">Select a team</option>
                            {allTeamsList.map((team) => (
                              <option key={team.id} value={team.id}>
                                {team.name} - Current: {team.provinceDisplay}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="newTeamProvince" className="block text-sm font-medium text-gray-700">
                          New Province/Territory
                        </label>
                        <div className="mt-1">
                          <select
                            id="newTeamProvince"
                            name="newTeamProvince"
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                            value={newTeamProvince}
                            onChange={(e) => setNewTeamProvince(e.target.value)}
                            required
                          >
                            <option value="">Select Province/Territory</option>
                            {Object.entries(provinceNames).map(([code, name]) => (
                              <option key={code} value={code}>{name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <button
                          type="submit"
                          disabled={actionLoading}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          {actionLoading ? 'Updating...' : 'Update Team Province'}
                        </button>
                      </div>
                    </form>
                  </div>
                        </div>
                      </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Export Data Tab */}
        {activeTab === 'export' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Export Data</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Export challenge data for analysis
              </p>
            </div>
            
            {successMessage && (
              <div className="mx-6 mt-4 bg-green-50 border-l-4 border-green-500 p-4">
                <p className="text-green-700">{successMessage}</p>
              </div>
            )}
            
            {actionError && (
              <div className="mx-6 mt-4 bg-red-50 border-l-4 border-red-500 p-4">
                <p className="text-red-700">{actionError}</p>
              </div>
            )}
            
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="space-y-6">
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Export Users</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Export all user data including names, provinces, and registration dates
                  </p>
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => handleExportData('users')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                  >
                    {actionLoading ? 'Exporting...' : 'Export CSV'}
                  </button>
                </div>
                
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Export Teams</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Export all team data including team names, provinces, and members
                  </p>
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => handleExportData('teams')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                  >
                    {actionLoading ? 'Exporting...' : 'Export CSV'}
                  </button>
                </div>
                
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Export Miles Data</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Export all miles logged including dates and user details
                  </p>
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => handleExportData('miles')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                  >
                    {actionLoading ? 'Exporting...' : 'Export CSV'}
                  </button>
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900">Comprehensive Data Export</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Export complete user data with all associated information in a single file.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleExportData('complete')}
                    disabled={actionLoading}
                    className="mt-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    {actionLoading ? 'Exporting...' : 'Export Complete User Data'}
                  </button>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Admin Tools</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Additional administrative tools to manage user data.
                  </p>
                  
                  <button
                    type="button"
                    onClick={syncEmailsFromAuth}
                    disabled={actionLoading}
                    className="mt-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    {actionLoading ? 'Syncing...' : 'Sync User Emails from Auth'}
                  </button>
                  <p className="mt-1 text-xs text-gray-500">
                    This will update all Firestore user documents with email addresses from Firebase Authentication.
                    Run this once to ensure all email addresses are available for export.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 